import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { 
  FiPackage, 
  FiTag, 
  FiInfo, 
  FiDollarSign, 
  FiLayers, 
  FiMapPin, 
  FiPercent, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiChevronRight,
  FiImage,
  FiPlus,
  FiSave,
  FiX
} from "react-icons/fi";
import api from "../services/api";
import ImageDropzone from "../components/ImageDropzone";
import { NIGER_CITIES } from "../constants/nigerCities";

let watermarkImagePromise = null;

const loadWatermarkImage = () => {
  if (!watermarkImagePromise) {
    watermarkImagePromise = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = "/watermark.png";
    });
  }
  return watermarkImagePromise;
};

const compressImageFile = (file, { maxDimension = 1600, quality = 0.82 } = {}) =>
  new Promise((resolve) => {
    if (!(file instanceof File) || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = async () => {
        const longestSide = Math.max(image.width, image.height);
        const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext("2d");
        if (!context) {
          resolve(file);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        try {
          const watermark = await loadWatermarkImage();
          const watermarkWidth = Math.max(72, Math.round(canvas.width * 0.16));
          const watermarkHeight = Math.round(
            watermarkWidth * (watermark.height / watermark.width)
          );
          const margin = Math.max(10, Math.round(canvas.width * 0.02));
          context.drawImage(
            watermark,
            canvas.width - watermarkWidth - margin,
            margin,
            watermarkWidth,
            watermarkHeight
          );
        } catch (_) {
          // If watermark loading fails, preserve the upload flow instead of blocking it.
        }
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(
              new File([blob], file.name, {
                type: blob.type || file.type || "image/jpeg",
                lastModified: Date.now(),
              })
            );
          },
          file.type === "image/png" ? "image/png" : "image/jpeg",
          quality
        );
      };
      image.onerror = () => resolve(file);
      image.src = typeof reader.result === "string" ? reader.result : "";
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

const optimizeUploadFile = async (file) => {
  if (!(file instanceof File)) {
    return file;
  }
  if (file.type.startsWith("image/")) {
    return compressImageFile(file);
  }
  return file;
};

const fieldClassName =
  "mt-2 w-full rounded-2xl border border-component-border bg-white px-5 py-4 text-sm font-bold text-gray-800 shadow-sm outline-none transition-all focus:border-[#ff7a00] focus:ring-4 focus:ring-[#ff7a00]/5 placeholder:text-gray-300";

const AstroidIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12.983 21.186a1 1 0 0 1-1.966 0 10 10 0 0 0-8.203-8.203 1 1 0 0 1 0-1.966 10 10 0 0 0 8.203-8.203 1 1 0 0 1 1.966 0 10 10 0 0 0 8.203 8.203 1 1 0 0 1 0 1.966 10 10 0 0 0-8.203 8.203" />
  </svg>
);

const ProductFormPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const isAdminEditing = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return (
      searchParams.get("admin") === "1" ||
      Boolean(user?.is_staff || user?.is_superuser)
    );
  }, [location.search, user]);
  const hasLoadedEditDataRef = useRef(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    extra_information: "",
    price: "",
    quantity_in_stock: "",
    minimum_order_quantity: "1",
    category_id: "",
    location: "Niamey",
    address: "",
    discount_percentage: "0",
  });
  const [thumbnail, setThumbnail] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [existingThumbnail, setExistingThumbnail] = useState("");
  const [existingGalleryMedia, setExistingGalleryMedia] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mainCategoryId, setMainCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState("");
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [error, setError] = useState("");
  const [supportsCod, setSupportsCod] = useState(true);
  const [supportsMeetup, setSupportsMeetup] = useState(true);
  const [codFee, setCodFee] = useState("0");
  const [productSelections, setProductSelections] = useState([]);
  const [generatingDescription, setGeneratingDescription] = useState(false);

  const normalizeSelectionForForm = useCallback((selection) => {
    if (!selection || typeof selection !== "object") {
      return null;
    }
    const prices =
      selection.prices && typeof selection.prices === "object"
        ? selection.prices
        : {};
    const valueList = Array.isArray(selection.values)
      ? selection.values
          .map((value) => value?.toString().trim())
          .filter(Boolean)
      : Array.isArray(selection.options)
      ? selection.options
          .map((option) =>
            typeof option === "string"
              ? option.trim()
              : option?.value?.toString().trim()
          )
          .filter(Boolean)
      : [];
    if (!selection.name || valueList.length === 0) {
      return null;
    }
    return {
      name: selection.name.toString(),
      options: valueList.map((value) => {
        const legacyOption = Array.isArray(selection.options)
          ? selection.options.find(
              (option) =>
                typeof option === "object" &&
                option?.value?.toString().trim() === value
            )
          : null;
        return {
          value,
          price:
            (prices?.[value] ??
              (legacyOption && typeof legacyOption === "object"
                ? legacyOption.price
                : ""))?.toString() || "",
        };
      }),
    };
  }, []);
  
  const sortedCategories = useMemo(
    () =>
      categories.slice().sort((a, b) => {
        if ((a.level || 0) === (b.level || 0)) {
          return a.name.localeCompare(b.name);
        }
        return (a.level || 0) - (b.level || 0);
      }),
    [categories]
  );
  const parentCategories = sortedCategories.filter((category) => !category.parent_id);
  const childCategories = sortedCategories.filter(
    (category) => String(category.parent_id || "") === String(mainCategoryId || "")
  );

  const getCategoryName = useCallback((category) => {
    if (!category) return "";
    const lang = i18n.language === "fr" ? "fr" : "en";
    return category[`name_${lang}`] || category.name;
  }, [i18n.language]);

  const syncCategorySelection = useCallback((categoryId, availableCategories) => {
    const source = availableCategories || [];
    const selected = source.find((category) => String(category.id) === String(categoryId || ""));
    if (!selected) {
      setMainCategoryId("");
      setSubcategoryId("");
      return;
    }
    if (selected.parent_id) {
      setMainCategoryId(String(selected.parent_id));
      setSubcategoryId(String(selected.id));
    } else {
      setMainCategoryId(String(selected.id));
      setSubcategoryId("");
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (isEditMode && hasLoadedEditDataRef.current) {
      return;
    }
    const currentUserId = user?.id;
    const currentUserIsAdmin = Boolean(user?.is_staff || user?.is_superuser);
    try {
      const categoriesRes = await api.get("/categories/");
      const loadedCategories = categoriesRes.data.results || categoriesRes.data;
      setCategories(loadedCategories);

      if (isEditMode) {
        const productRes = await api.get(`/products/${id}/`);
        const productData = productRes.data;
        const ownerId = productData?.supplier?.id;
        if (
          ownerId &&
          ownerId !== currentUserId &&
          !currentUserIsAdmin &&
          !isAdminEditing
        ) {
          navigate("/");
          return;
        }

        const currentData = {
          name: productData.name,
          description: productData.description,
          extra_information: productData.extra_information || "",
          price: productData.price || productData.max_price || productData.min_price || "",
          quantity_in_stock: productData.quantity_in_stock,
          minimum_order_quantity: productData.minimum_order_quantity,
          category_id: productData.category?.id || "",
          location: productData.location || "Niamey",
          address: productData.address || "",
          discount_percentage: productData.discount_percentage || "0",
        };

        setFormData(currentData);
        syncCategorySelection(currentData.category_id, loadedCategories);
        setExistingThumbnail(productData.thumbnail_url || "");
        setExistingGalleryMedia(productData.images || []);
        setProductSelections(
          Array.isArray(productData.selections)
            ? productData.selections
                .map((selection) => normalizeSelectionForForm(selection))
                .filter(Boolean)
            : []
        );
        const deliveryMethods = Array.isArray(productData.delivery_methods)
          ? productData.delivery_methods
          : [];
        const cod = deliveryMethods.find((method) => method.code === "cash_on_delivery");
        const meetup = deliveryMethods.find((method) => method.code === "meetup");
        if (deliveryMethods.length === 0) {
          setSupportsCod(true);
          setSupportsMeetup(true);
          setCodFee("0");
        } else {
          setSupportsCod(Boolean(cod?.enabled));
          setSupportsMeetup(Boolean(meetup?.enabled));
          setCodFee((cod?.fee ?? "0").toString());
        }
        hasLoadedEditDataRef.current = true;
      }
    } catch (err) {
      console.error("Failed to fetch product data or categories:", err);
      setError(t("productForm.dataLoadError"));
    } finally {
      if (isEditMode) {
        setPageLoading(false);
      }
    }
  }, [
    id,
    isEditMode,
    navigate,
    normalizeSelectionForForm,
    syncCategorySelection,
    t,
    user?.id,
    user?.is_staff,
    user?.is_superuser,
    isAdminEditing,
  ]);

  useEffect(() => {
    if (authLoading) return;
    fetchData();
  }, [authLoading, fetchData]);

  useEffect(() => {
    hasLoadedEditDataRef.current = false;
    if (isEditMode) {
      setPageLoading(true);
    }
  }, [id, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "main_category_id") {
      setMainCategoryId(value);
      setSubcategoryId("");
      setFormData((prev) => ({
        ...prev,
        category_id: value,
      }));
      return;
    }
    if (name === "subcategory_id") {
      setSubcategoryId(value);
      setFormData((prev) => ({
        ...prev,
        category_id: value || mainCategoryId,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectionChange = (index, field, value) => {
    setProductSelections((prev) =>
      prev.map((selection, currentIndex) =>
        currentIndex === index ? { ...selection, [field]: value } : selection
      )
    );
  };

  const handleSelectionOptionChange = (selectionIndex, optionIndex, field, value) => {
    setProductSelections((prev) =>
      prev.map((selection, currentIndex) => {
        if (currentIndex !== selectionIndex) return selection;
        return {
          ...selection,
          options: (selection.options || []).map((option, currentOptionIndex) =>
            currentOptionIndex === optionIndex ? { ...option, [field]: value } : option
          ),
        };
      })
    );
  };

  const addSelectionRow = () => {
    setProductSelections((prev) => [
      ...prev,
      { name: "Size", options: [{ value: "", price: "" }] },
    ]);
  };

  const removeSelectionRow = (index) => {
    setProductSelections((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const addSelectionOptionRow = (selectionIndex) => {
    setProductSelections((prev) =>
      prev.map((selection, currentIndex) =>
        currentIndex === selectionIndex
          ? {
              ...selection,
              options: [...(selection.options || []), { value: "", price: "" }],
            }
          : selection
      )
    );
  };

  const removeSelectionOptionRow = (selectionIndex, optionIndex) => {
    setProductSelections((prev) =>
      prev.map((selection, currentIndex) => {
        if (currentIndex !== selectionIndex) return selection;
        return {
          ...selection,
          options: (selection.options || []).filter(
            (_, currentOptionIndex) => currentOptionIndex !== optionIndex
          ),
        };
      })
    );
  };

  const handleGenerateDescription = async () => {
    if (generatingDescription || loading) return;

    const productName = formData.name.trim();
    if (!productName) {
      showNotification(t("productForm.aiDescriptionNameRequired"), "danger");
      return;
    }

    const selectedCategoryId = subcategoryId || mainCategoryId || formData.category_id;
    const selectedCategory = categories.find(
      (category) => String(category.id) === String(selectedCategoryId || "")
    );
    if (!selectedCategory) {
      showNotification(t("productForm.aiDescriptionCategoryRequired"), "danger");
      return;
    }

    const selectedMainCategory = mainCategoryId
      ? categories.find((category) => String(category.id) === String(mainCategoryId))
      : selectedCategory;
    const mainCategoryName = getCategoryName(selectedMainCategory || selectedCategory);
    const subcategoryName =
      selectedMainCategory && selectedCategory.id !== selectedMainCategory.id
        ? getCategoryName(selectedCategory)
        : "";

    setGeneratingDescription(true);
    try {
      const response = await api.post("/ai/product-description/", {
        name: productName,
        price: formData.price || "",
        category: mainCategoryName,
        subcategory: subcategoryName,
        location: formData.location || "Niger",
      });
      const description = (response.data?.description || "").trim();
      if (!description) {
        throw new Error(t("productForm.aiDescriptionEmpty"));
      }
      setFormData((prev) => ({
        ...prev,
        description,
      }));
      showNotification(t("productForm.aiDescriptionApplied"), "success");
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        t("productForm.aiDescriptionError");
      showNotification(message, "danger");
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setUploadProgress(0);
    setUploadStage(t("productForm.optimizingMedia"));

    const quantityInStock = parseInt(formData.quantity_in_stock, 10);
    const minimumOrderQuantity = parseInt(formData.minimum_order_quantity, 10);
    if (minimumOrderQuantity > quantityInStock) {
      setError(t("productForm.minOrderQuantityError"));
      setLoading(false);
      return;
    }

    if (!mainCategoryId) {
      setError("Please select a main category.");
      setLoading(false);
      return;
    }

    if (childCategories.length > 0 && !subcategoryId) {
      setError("Please select a subcategory.");
      setLoading(false);
      return;
    }
    if (!supportsCod && !supportsMeetup) {
      setError(t("productForm.deliveryMethodError"));
      setLoading(false);
      return;
    }

    const productData = new FormData();
    const resolvedCategoryId = subcategoryId || mainCategoryId;
    const normalizedSelections = productSelections
      .map((selection) => ({
        name: selection.name.trim(),
        values: (selection.options || [])
          .map((option) => option.value.trim())
          .filter(Boolean),
        prices: Object.fromEntries(
          (selection.options || [])
            .map((option) => ({
              value: option.value.trim(),
              price: option.price.trim(),
            }))
            .filter((option) => option.value && option.price)
            .map((option) => [option.value, option.price])
        ),
      }))
      .filter((selection) => selection.name && selection.values.length > 0);
    const deliveryMethods = [
      ...(supportsCod
        ? [
            {
              code: "cash_on_delivery",
              label: "Cash on Delivery",
              fee: codFee.trim() || "0",
              enabled: true,
              coming_soon: false,
            },
          ]
        : []),
      ...(supportsMeetup
        ? [
            {
              code: "meetup",
              label: "Face to Face Meetup",
              fee: "0",
              enabled: true,
              coming_soon: false,
            },
          ]
        : []),
    ];
    const optimizedThumbnail =
      thumbnail.length > 0 ? await optimizeUploadFile(thumbnail[0]) : null;
    const optimizedGalleryMedia = await Promise.all(
      galleryImages.map((file) => optimizeUploadFile(file))
    );

    if (isEditMode) {
      Object.keys(formData).forEach((key) => productData.append(key, formData[key]));
      productData.set("category_id", resolvedCategoryId);
      productData.set("selections", JSON.stringify(normalizedSelections));
      productData.set("delivery_methods", JSON.stringify(deliveryMethods));
      if (optimizedThumbnail) {
        productData.append("thumbnail", optimizedThumbnail, optimizedThumbnail.name);
      }
      optimizedGalleryMedia.forEach((mediaFile) => {
        productData.append("images", mediaFile, mediaFile.name);
      });
    } else {
      if (thumbnail.length === 0) {
        setError(t("productForm.thumbnailRequiredError"));
        setLoading(false);
        return;
      }
      Object.keys(formData).forEach((key) => productData.append(key, formData[key]));
      productData.set("category_id", resolvedCategoryId);
      productData.set("selections", JSON.stringify(normalizedSelections));
      productData.set("delivery_methods", JSON.stringify(deliveryMethods));
      productData.append("thumbnail", optimizedThumbnail, optimizedThumbnail.name);
      optimizedGalleryMedia.forEach((mediaFile) =>
        productData.append("images", mediaFile, mediaFile.name)
      );
    }

    try {
      setUploadStage(t("productForm.uploadingMedia"));
      const response = isEditMode
        ? await api.patch(`/products/${id}/`, productData, {
            onUploadProgress: (event) => {
              if (!event.total) return;
              setUploadProgress(Math.round((event.loaded / event.total) * 100));
            },
          })
        : await api.post("/products/", productData, {
            onUploadProgress: (event) => {
              if (!event.total) return;
              setUploadProgress(Math.round((event.loaded / event.total) * 100));
            },
          });
      showNotification(
        isEditMode ? t("productForm.updatedSuccess") : "Your product is under review!! It will be visible once approved by an admin.",
        "success"
      );
      if (isEditMode) {
        navigate(`/products/${response.data.id}`);
      } else {
        navigate("/profile?tab=products");
      }
    } catch (err) {
      console.error("Failed to save product:", err.response?.data || err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (typeof err.response?.data === "object") {
        const firstError = Object.values(err.response.data)[0];
        setError(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        setError(t("productForm.saveError"));
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setUploadStage("");
    }
  };

  if (pageLoading && isEditMode) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 border-4 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin" />
        <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.4em] animate-pulse">Synchronizing Data Assets...</p>
      </div>
    );
  }

  return (
    <div className="bg-theme-bg min-h-screen py-10 md:py-16 px-4">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[1fr_400px]">
          {/* Main Studio Area */}
          <main className="space-y-12">
            <header className="space-y-4">
              <div className="flex items-center gap-3 text-[#ff7a00] font-black text-[10px] uppercase tracking-[0.3em]">
                <FiPackage className="text-lg" />
                <span>{t("navbar.sell")}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                {isEditMode ? t("productForm.editProduct") : t("productForm.addProduct")}
              </h1>
              <p className="text-gray-400 font-medium max-w-2xl leading-relaxed">
                {t("productForm.formSubtitle") || "Fill in the details below to list your product on the Chafadi marketplace. Provide clear photos and accurate pricing to attract more buyers."}
              </p>
            </header>

            <form onSubmit={handleSubmit} className="space-y-12 bg-white rounded-[40px] border border-component-border p-8 md:p-12 shadow-soft">
              {error && (
                <div className="bg-rose-50/50 text-rose-500 p-6 rounded-3xl border border-rose-100 flex items-center gap-4 animate-shake">
                  <FiAlertCircle className="text-2xl shrink-0" />
                  <div className="text-xs font-black uppercase tracking-widest leading-relaxed">{error}</div>
                </div>
              )}

              {loading && (
                <div className="rounded-[28px] border border-orange-100 bg-gradient-to-r from-orange-50 via-white to-amber-50 p-6 shadow-sm">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-chafadi-primary">
                        {uploadStage || t("productForm.uploadingMedia")}
                      </p>
                      <p className="mt-2 text-sm font-bold text-theme-text">
                        {t("productForm.uploadProgress", { progress: uploadProgress })}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-full border-4 border-orange-100 border-t-chafadi-primary animate-spin" />
                  </div>
                  <div className="mt-5 h-3 rounded-full bg-orange-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-chafadi-primary to-amber-400 transition-all duration-300"
                      style={{ width: `${Math.max(uploadProgress, 8)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Core Identity */}
              <section className="space-y-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-[#fcfcfc] border border-gray-100 flex items-center justify-center text-[#ff7a00]">
                    <FiTag />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t("productForm.basicInfo") || "Product Information"}</h2>
                </div>
                
                <div className="grid gap-8 md:grid-cols-2">
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">{t("productForm.productName")}</span>
                    <input
                      className={fieldClassName}
                      type="text"
                      name="name"
                      placeholder="e.g. Premium Basmati Rice"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </label>

                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">Main Category</span>
                    <div className="relative">
                      <select
                        className={`${fieldClassName} appearance-none pr-12`}
                        name="main_category_id"
                        value={mainCategoryId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a main category</option>
                        {parentCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {getCategoryName(cat)}
                          </option>
                        ))}
                      </select>
                      <FiLayers className="absolute right-5 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none" />
                    </div>
                  </label>

                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">Subcategory</span>
                    <div className="relative">
                      <select
                        className={`${fieldClassName} appearance-none pr-12`}
                        name="subcategory_id"
                        value={subcategoryId}
                        onChange={handleInputChange}
                        disabled={!mainCategoryId || childCategories.length === 0}
                        required={childCategories.length > 0}
                      >
                        <option value="">
                          {childCategories.length > 0 ? "Select a subcategory" : "No subcategory required"}
                        </option>
                        {childCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {getCategoryName(cat)}
                          </option>
                        ))}
                      </select>
                      <FiLayers className="absolute right-5 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none" />
                    </div>
                  </label>
                </div>

                <label className="block space-y-2">
                  <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">{t("productForm.description")}</span>
                  <div className="relative">
                    <textarea
                      className={`${fieldClassName} min-h-[170px] resize-none py-5 pr-20`}
                      name="description"
                      placeholder={t("productForm.descriptionPlaceholder")}
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={handleGenerateDescription}
                      disabled={generatingDescription || loading}
                      className="absolute bottom-4 right-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ff7a00] text-white shadow-xl shadow-orange-500/25 transition-all hover:bg-[#e66c00] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label={t("productForm.aiWrite")}
                      title={t("productForm.aiWrite")}
                    >
                      {generatingDescription ? (
                        <span className="h-5 w-5 rounded-full border-2 border-white/35 border-t-white animate-spin" />
                      ) : (
                        <AstroidIcon className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                </label>

                <div className="space-y-5 md:col-span-2">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-black text-gray-900 uppercase tracking-[0.18em]">
                        {t("productForm.selectionsTitle")}
                      </h3>
                      <p className="mt-2 text-xs font-bold text-gray-400">
                        {t("productForm.selectionsHint")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addSelectionRow}
                      className="inline-flex h-11 items-center gap-2 rounded-full border border-[#ff7a00]/20 bg-white px-4 text-[11px] font-black uppercase tracking-[0.18em] text-[#ff7a00] transition-all hover:border-[#ff7a00] hover:bg-orange-50"
                    >
                      <FiPlus size={14} />
                      {t("productForm.addSelection")}
                    </button>
                  </div>

                  {productSelections.length === 0 ? (
                    <div className="border-b border-dashed border-gray-200 pb-5 text-xs font-bold text-gray-400">
                      {t("productForm.noSelections")}
                    </div>
                  ) : (
                    <div className="space-y-0 border-y border-gray-100">
                      {productSelections.map((selection, index) => (
                        <div
                          key={`selection-${index}`}
                          className="border-b border-gray-100 py-5 last:border-b-0"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <label className="block flex-1 space-y-2">
                              <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">
                                {t("productForm.selectionName")}
                              </span>
                              <input
                                className={fieldClassName}
                                type="text"
                                value={selection.name}
                                onChange={(e) =>
                                  handleSelectionChange(index, "name", e.target.value)
                                }
                                placeholder={t("productForm.selectionNamePlaceholder")}
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => removeSelectionRow(index)}
                              className="mt-7 inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-rose-100 bg-rose-50 text-rose-500 transition-all hover:bg-rose-100"
                              aria-label={t("productForm.removeSelection")}
                            >
                              <FiX size={18} />
                            </button>
                          </div>

                          <div className="mt-4 space-y-3">
                            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
                              <div className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">
                                {t("productForm.selectionValues")}
                              </div>
                              <div className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">
                                {t("productForm.selectionPrice")}
                              </div>
                            </div>

                            {(selection.options || []).map((option, optionIndex) => (
                              <div
                                key={`selection-${index}-option-${optionIndex}`}
                                className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px_auto]"
                              >
                                <input
                                  className={fieldClassName}
                                  type="text"
                                  value={option.value}
                                  onChange={(e) =>
                                    handleSelectionOptionChange(
                                      index,
                                      optionIndex,
                                      "value",
                                      e.target.value
                                    )
                                  }
                                  placeholder={t("productForm.selectionValuesPlaceholder")}
                                />
                                <input
                                  className={fieldClassName}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={option.price}
                                  onChange={(e) =>
                                    handleSelectionOptionChange(
                                      index,
                                      optionIndex,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  placeholder={t("productForm.selectionPricePlaceholder")}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeSelectionOptionRow(index, optionIndex)}
                                  className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 transition-all hover:border-rose-200 hover:bg-rose-50 hover:text-rose-500"
                                  aria-label={t("productForm.removeSelectionValue")}
                                >
                                  <FiX size={18} />
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() => addSelectionOptionRow(index)}
                              className="inline-flex h-11 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-[11px] font-black uppercase tracking-[0.16em] text-gray-600 transition-all hover:border-[#ff7a00]/35 hover:text-[#ff7a00]"
                            >
                              <FiPlus size={14} />
                              {t("productForm.addSelectionValue")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <label className="block space-y-2">
                  <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">{t("productForm.extraInfo")}</span>
                  <textarea
                    className={`${fieldClassName} min-h-[120px] resize-none py-5`}
                    name="extra_information"
                    value={formData.extra_information}
                    onChange={handleInputChange}
                    placeholder="Shipping logistics, specialized handling, or dynamic pricing notes..."
                  />
                </label>
              </section>

              {/* Economic Parameters */}
              <section className="space-y-8 pt-8 border-t border-component-border">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-[#fcfcfc] border border-gray-100 flex items-center justify-center text-[#ff7a00]">
                    <FiDollarSign />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t("productForm.pricingTitle") || "Pricing & Inventory"}</h2>
                </div>

                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">Price (FcFa)</span>
                    <input className={fieldClassName} type="number" step="0.01" name="price" value={formData.price} onChange={handleInputChange} required />
                  </label>
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">{t("productForm.quantity")}</span>
                    <input className={fieldClassName} type="number" min="1" name="quantity_in_stock" value={formData.quantity_in_stock} onChange={handleInputChange} required />
                  </label>
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">{t("productForm.minOrder")}</span>
                    <input className={fieldClassName} type="number" min="1" name="minimum_order_quantity" value={formData.minimum_order_quantity} onChange={handleInputChange} required />
                  </label>
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">Discount %</span>
                    <div className="relative">
                      <input className={`${fieldClassName} pr-12`} type="number" min="0" max="100" name="discount_percentage" value={formData.discount_percentage} onChange={handleInputChange} />
                      <FiPercent className="absolute right-5 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none" />
                    </div>
                  </label>
                </div>
              </section>

              {/* Logistical Vector */}
              <section className="space-y-8 pt-8 border-t border-component-border">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-[#fcfcfc] border border-gray-100 flex items-center justify-center text-[#ff7a00]">
                    <FiMapPin />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{isEditMode ? t("productForm.locationTitle") || "Location Details" : t("productForm.locationTitle") || "Product Location"}</h2>
                </div>

                <div className="grid gap-8 md:grid-cols-2">
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">Operational City</span>
                    <div className="relative">
                      <select className={`${fieldClassName} appearance-none pr-12`} name="location" value={formData.location} onChange={handleInputChange} required>
                        {NIGER_CITIES.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      <FiChevronRight className="absolute right-5 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none rotate-90" />
                    </div>
                  </label>
                  <label className="block space-y-2">
                    <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">Full address (optional)</span>
                    <input
                      className={fieldClassName}
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Street, district, landmark"
                    />
                  </label>
                </div>
              </section>

              <section className="space-y-8 pt-8 border-t border-component-border">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-theme-bg flex items-center justify-center text-chafadi-primary">
                    <FiCheckCircle />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t("productForm.deliveryMethods")}
                  </h2>
                </div>

                <div className="grid gap-8 md:grid-cols-2">
                  <div className="rounded-[28px] border border-component-border bg-theme-bg/40 p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-black text-theme-text uppercase tracking-[0.2em]">
                          {t("productForm.cashOnDelivery")}
                        </h3>
                        <p className="mt-2 text-xs font-medium text-theme-muted">
                          {t("productForm.codFeeHint")}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 accent-chafadi-primary"
                        checked={supportsCod}
                        onChange={(e) => setSupportsCod(e.target.checked)}
                      />
                    </div>
                    <label className="block space-y-2">
                      <span className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] ml-1">
                        {t("productForm.codFee")}
                      </span>
                      <input
                        className={fieldClassName}
                        type="number"
                        min="0"
                        step="0.01"
                        value={codFee}
                        onChange={(e) => setCodFee(e.target.value)}
                        disabled={!supportsCod}
                      />
                    </label>
                  </div>

                  <div className="rounded-[28px] border border-component-border bg-theme-bg/40 p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-black text-theme-text uppercase tracking-[0.2em]">
                          {t("productForm.faceToFaceMeetup")}
                        </h3>
                        <p className="mt-2 text-xs font-medium text-theme-muted">
                          {t("productForm.faceToFaceHint")}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 accent-chafadi-primary"
                        checked={supportsMeetup}
                        onChange={(e) => setSupportsMeetup(e.target.checked)}
                      />
                    </div>
                    <div className="rounded-2xl border border-dashed border-component-border px-4 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-theme-muted">
                      {t("productForm.noChargesByDefault")}
                    </div>
                  </div>
                </div>
              </section>

              {/* Media Infrastructure */}
              <section className="space-y-8 pt-8 border-t border-component-border">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-[#fcfcfc] border border-gray-100 flex items-center justify-center text-[#ff7a00]">
                    <FiImage />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t("productForm.mediaTitle") || "Product Media"}</h2>
                </div>

                <div className="grid gap-12 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{isEditMode ? t("productForm.changeMainImage") || "Change Image" : t("productForm.mainImageTitle") || "Main Image"}</span>
                      <span className="text-[10px] font-bold text-[#ff7a00] bg-orange-50 px-3 py-1 rounded-full uppercase tracking-widest">{t("common.required") || "Required"}</span>
                    </div>
                    {isEditMode && existingThumbnail && thumbnail.length === 0 && (
                      <div className="relative group rounded-[2.5rem] overflow-hidden border-2 border-theme-bg shadow-inner">
                        <img src={existingThumbnail} alt="" className="h-64 w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                           <span className="px-6 py-2 bg-white rounded-full text-[10px] font-black uppercase tracking-widest text-theme-text shadow-xl">Current Master</span>
                        </div>
                      </div>
                    )}
                    <ImageDropzone
                      files={thumbnail}
                      setFiles={setThumbnail}
                      maxFiles={1}
                      allowVideo={false}
                    />
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-theme-muted">
                      {t("productForm.mainImageOnly")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t("productForm.galleryExpansion") || "Extra Photos & Video"}</span>
                      <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-full uppercase tracking-widest">{t("common.optional") || "Optional"}</span>
                    </div>
                    {isEditMode && existingGalleryMedia.length > 0 && galleryImages.length === 0 && (
                      <div className="grid grid-cols-4 gap-3">
                        {existingGalleryMedia.map((media) => (
                          <div key={media.id} className="relative aspect-square rounded-2xl overflow-hidden border border-component-border">
                            {(media.media_type || "image") === "video" ? (
                              <video
                                src={media.image_url || media.media_url}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                                controls
                              />
                            ) : (
                              <img src={media.image_url || media.media_url} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <ImageDropzone
                      files={galleryImages}
                      setFiles={setGalleryImages}
                      maxFiles={5}
                      allowVideo
                    />
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-theme-muted">
                      {t("productForm.galleryMediaHint")}
                    </p>
                  </div>
                </div>
              </section>

              {/* Execution Bar */}
              <div className="pt-8 border-t border-component-border flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-3 text-theme-muted">
                  <FiCheckCircle className="text-emerald-500" />
                  <p className="text-[10px] font-black uppercase tracking-widest">Ready to publish</p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto h-16 px-12 bg-[#ff7a00] text-white font-black rounded-2xl shadow-xl shadow-orange-500/20 hover:bg-[#e66c00] transition-all flex items-center justify-center gap-4 active:scale-95 disabled:opacity-50 uppercase tracking-widest text-xs"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {isEditMode ? <FiSave className="text-lg" /> : <FiPlus className="text-lg" />}
                      {isEditMode ? t("productForm.saveChanges") : t("productForm.listProduct")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </main>

          {/* Tactical Intelligence */}
          <aside className="space-y-8">
            <div className="sticky top-32 space-y-8">
              <section className="bg-white rounded-[40px] border border-component-border p-8 shadow-soft space-y-6">
                <div className="w-12 h-12 rounded-2xl bg-[#fcfcfc] flex items-center justify-center text-[#ff7a00] text-xl shadow-inner border border-gray-100">
                   <FiInfo />
                </div>
                <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-[0.3em]">{t("productForm.sellerTips") || "Pro Seller Tips"}</h3>
                <ul className="space-y-6">
                  {(t("productForm.sellerTipsList", { returnObjects: true }) || [
                    "Use clear, high-quality images to attract more buyers.",
                    "Write a detailed description to help customers find your product.",
                    "Accurate location data helps build trust with local buyers.",
                    "Use discounts to encourage bulk purchases."
                  ]).map((note, i) => (
                    <li key={i} className="flex gap-4 items-start text-xs font-medium text-theme-muted leading-relaxed">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#ff7a00] shrink-0 mt-1.5" />
                      {note}
                    </li>
                  ))}
                </ul>
              </section>

                <div className="bg-gray-900 rounded-[40px] p-8 text-white space-y-4 shadow-2xl">
                   <p className="text-[10px] font-black text-[#ff7a00] uppercase tracking-[0.4em]">{t("common.important") || "Important"}</p>
                   <h4 className="text-xl font-bold tracking-tight">{t("aboutPage.terms") || "Marketplace Policy"}</h4>
                   <p className="text-white/60 text-xs font-medium leading-relaxed">
                     {t("productForm.termsNote") || "By listing this product, you agree to Chafadi's terms of service. All items are reviewed to ensure quality and safety for the community."}
                   </p>
                </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProductFormPage;
